import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo, useState, type ReactNode } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  Calendar,
  CheckCircle2,
  Link2,
  Pencil,
  Trash2,
  UserPlus,
  XCircle,
} from 'lucide-react';
import clsx from 'clsx';
import { apiRequest, ApiError } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';
import { useToast } from '../lib/toast';
import { useConfirm } from '../components/ui/Confirm';
import { Button } from '../components/ui/Button';
import { Skeleton } from '../components/ui/Skeleton';
import { EmptyState } from '../components/ui/EmptyState';
import { GatedSection } from '../components/shared/GatedSection';
import { DetailHeader } from '../components/shared/DetailHeader';
import { KpiStrip, type KpiItem } from '../components/shared/KpiStrip';
import { RelationPanel } from '../components/shared/RelationPanel';
import { ActivityPanel } from '../components/shared/ActivityPanel';
import { AddRelationModal } from '../components/shared/AddRelationModal';
import {
  EntityPicker,
  type PickedEntity,
} from '../components/shared/EntityPicker';
import { formatDate, timeAgo } from '../lib/format';
import {
  EditInterviewSlotDrawer,
  INTERVIEW_DECISION_LABEL,
  SLOT_MODE_LABEL,
  SLOT_STATUS_LABEL,
  SLOT_STATUS_TONE,
  STAGE_TONE_CLASSES,
  type InterviewBookingWithCandidate,
  type InterviewEvaluation,
  type InterviewSlotDetailResponse,
} from '../components/hiring';

export const Route = createFileRoute('/_authed/hr/interviews/$slotId')({
  component: InterviewSlotDetailPage,
});

type Tab = 'overview' | 'bookings' | 'evaluations' | 'activity';

const TABS: ReadonlyArray<{ value: Tab; label: string }> = [
  { value: 'overview', label: 'Overview' },
  { value: 'bookings', label: 'Bookings' },
  { value: 'evaluations', label: 'Evaluations' },
  { value: 'activity', label: 'Activity' },
];

const HR_ROLES: ReadonlyArray<string> = ['super_admin', 'academic_admin', 'hr'];

function InterviewSlotDetailPage() {
  const { slotId } = Route.useParams();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { confirm } = useConfirm();
  const role = useAuthStore((s) => s.user?.primaryRole ?? null);
  const isHr = role !== null && HR_ROLES.includes(role);
  const isSuperAdmin = role === 'super_admin';

  const [tab, setTab] = useState<Tab>('overview');
  const [editOpen, setEditOpen] = useState(false);
  const [bookOpen, setBookOpen] = useState(false);
  const [picked, setPicked] = useState<PickedEntity | null>(null);

  const detailQuery = useQuery({
    queryKey: ['hiring', 'interview-slot', slotId] as const,
    queryFn: ({ signal }) =>
      apiRequest<InterviewSlotDetailResponse>(
        `/api/hiring/interviews/slots/${encodeURIComponent(slotId)}`,
        { signal },
      ),
    retry: (failureCount, err) => {
      if (err instanceof ApiError && (err.status === 404 || err.status === 403)) {
        return false;
      }
      return failureCount < 2;
    },
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({
      queryKey: ['hiring', 'interview-slot', slotId],
    });
    void queryClient.invalidateQueries({ queryKey: ['hiring', 'interviews'] });
  };

  const cancelMutation = useMutation({
    mutationFn: () =>
      apiRequest<{ ok: true; cancelledBookings: number }>(
        `/api/hiring/interviews/slots/${encodeURIComponent(slotId)}/cancel`,
        { method: 'POST', body: {} },
      ),
    onSuccess: (res) => {
      toast(
        res.cancelledBookings > 0
          ? `Slot cancelled. ${res.cancelledBookings} candidate booking(s) rolled back.`
          : 'Slot cancelled.',
        { variant: 'success' },
      );
      invalidate();
    },
    onError: (err: unknown) =>
      toast(err instanceof ApiError ? err.message : 'Could not cancel slot.', {
        variant: 'error',
      }),
  });

  const attendanceMutation = useMutation({
    mutationFn: (input: { bookingId: string; status: 'attended' | 'no_show' }) =>
      apiRequest<{ ok: true }>(
        `/api/hiring/bookings/${encodeURIComponent(input.bookingId)}/attendance`,
        { method: 'POST', body: { status: input.status } },
      ),
    onSuccess: (_res, vars) => {
      toast(
        vars.status === 'attended'
          ? 'Marked attended.'
          : 'Marked no-show.',
        { variant: 'success' },
      );
      invalidate();
    },
    onError: (err: unknown) =>
      toast(
        err instanceof ApiError ? err.message : 'Could not update attendance.',
        { variant: 'error' },
      ),
  });

  const releaseMutation = useMutation({
    mutationFn: (input: { bookingId: string; reason?: string }) =>
      apiRequest<{ ok: true }>(
        `/api/hiring/bookings/${encodeURIComponent(input.bookingId)}/release`,
        {
          method: 'POST',
          body: input.reason ? { reason: input.reason } : {},
        },
      ),
    onSuccess: () => {
      toast('Booking released. Candidate is back to aptitude_passed.', {
        variant: 'success',
      });
      invalidate();
    },
    onError: (err: unknown) =>
      toast(
        err instanceof ApiError ? err.message : 'Could not release booking.',
        { variant: 'error' },
      ),
  });

  const handleCancelSlot = async () => {
    const ok = await confirm({
      title: 'Cancel this slot?',
      description:
        'Any candidate bookings on this slot are cancelled and the candidate is bumped back to "aptitude passed" so they can re-book.',
      confirmLabel: 'Cancel slot',
      variant: 'danger',
    });
    if (!ok) return;
    cancelMutation.mutate();
  };

  const handleRelease = async (bookingId: string) => {
    const ok = await confirm({
      title: 'Release this booking?',
      description:
        'The candidate is rolled back to aptitude_passed and can re-book another slot.',
      confirmLabel: 'Release',
      variant: 'danger',
    });
    if (!ok) return;
    releaseMutation.mutate({ bookingId });
  };

  const handleCopyInvite = async () => {
    const link = detailQuery.data?.slot.locationOrLink;
    if (!link) {
      toast('No video call link set on this slot.', { variant: 'error' });
      return;
    }
    try {
      await navigator.clipboard.writeText(link);
      toast('Invite link copied to clipboard.', { variant: 'success' });
    } catch {
      toast('Could not copy link.', { variant: 'error' });
    }
  };

  const data = detailQuery.data;
  const slot = data?.slot;

  // Bookings the slot considers to be holding a seat. Cancelled and no-show
  // rows count toward history but not capacity.
  const activeBookings = useMemo(
    () =>
      data
        ? data.bookings.filter(
            (b) => b.status === 'booked' || b.status === 'attended',
          )
        : [],
    [data],
  );

  const attendedCount = useMemo(
    () => (data ? data.bookings.filter((b) => b.status === 'attended').length : 0),
    [data],
  );
  const noShowCount = useMemo(
    () => (data ? data.bookings.filter((b) => b.status === 'no_show').length : 0),
    [data],
  );
  const decidedEvaluations = useMemo(
    () => (data ? data.evaluations.filter((e) => e.decision !== 'pending').length : 0),
    [data],
  );

  const startsRelative = useMemo(() => {
    if (!slot) return '—';
    const startMs = new Date(slot.startsAt).getTime();
    if (!Number.isFinite(startMs)) return '—';
    const diff = startMs - Date.now();
    if (diff <= 0) {
      return `${timeAgo(slot.startsAt)}`;
    }
    const formatter = new Intl.RelativeTimeFormat(undefined, {
      numeric: 'auto',
    });
    const HOUR = 60 * 60 * 1000;
    const DAY = 24 * HOUR;
    if (diff < HOUR) {
      return formatter.format(Math.round(diff / 60_000), 'minute');
    }
    if (diff < DAY) {
      return formatter.format(Math.round(diff / HOUR), 'hour');
    }
    return formatter.format(Math.round(diff / DAY), 'day');
  }, [slot]);

  const kpis: KpiItem[] = useMemo(() => {
    if (!slot || !data) return [];
    const booked = activeBookings.length;
    const remaining = Math.max(0, slot.capacity - booked);
    return [
      {
        label: 'Booked / capacity',
        value: `${booked} / ${slot.capacity}`,
      },
      {
        label: 'Seats remaining',
        value: remaining,
      },
      {
        label: 'Attended',
        value: attendedCount,
      },
      {
        label: 'No-shows',
        value: noShowCount,
      },
      {
        label: 'Decided evaluations',
        value: `${decidedEvaluations} / ${data.evaluations.length}`,
      },
      {
        label: 'Starts in',
        value: startsRelative,
      },
    ];
  }, [
    slot,
    data,
    activeBookings.length,
    attendedCount,
    noShowCount,
    decidedEvaluations,
    startsRelative,
  ]);

  const canBook =
    isHr && slot !== undefined && slot.status === 'open';

  const handleBookSubmit = async () => {
    if (!picked || !picked.id) {
      throw new ApiError(
        400,
        'application_required',
        'Pick a candidate before booking.',
      );
    }
    const res = await apiRequest<{ id: string }>(
      `/api/hiring/interviews/slots/${encodeURIComponent(slotId)}/book`,
      { method: 'POST', body: { applicationId: picked.id } },
    );
    return res;
  };

  return (
    <div className="px-4 sm:px-6 py-6 max-w-screen-2xl mx-auto w-full">
      <GatedSection
        error={detailQuery.error}
        title="Interview slot"
        onRetry={() => detailQuery.refetch()}
      >
        {detailQuery.isLoading || !data || !slot ? (
          <div className="space-y-4">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : (
          <div className="space-y-6">
            <DetailHeader
              title={`Interview slot · ${formatDate(slot.startsAt, { withTime: true })}`}
              sublabel={
                <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                  <span>
                    ends {formatDate(slot.endsAt, { withTime: true })}
                  </span>
                  <span aria-hidden="true">·</span>
                  <span>{SLOT_MODE_LABEL[slot.mode]}</span>
                  {slot.locationOrLink ? (
                    <>
                      <span aria-hidden="true">·</span>
                      <span className="truncate max-w-[40ch]">
                        {slot.locationOrLink}
                      </span>
                    </>
                  ) : null}
                  <span aria-hidden="true">·</span>
                  <span>
                    with{' '}
                    <span className="font-medium text-[color:var(--color-text)]">
                      {data.interviewer?.displayName ??
                        `interviewer ${slot.interviewerId.slice(0, 8)}`}
                    </span>
                  </span>
                </span>
              }
              statusChip={
                <span
                  className={clsx(
                    'inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] uppercase tracking-wide',
                    STAGE_TONE_CLASSES[SLOT_STATUS_TONE[slot.status]],
                  )}
                >
                  {SLOT_STATUS_LABEL[slot.status]}
                </span>
              }
              backTo="/hr/interviews"
              actions={
                <>
                  {isHr ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={<Pencil size={14} aria-hidden="true" />}
                      onClick={() => setEditOpen(true)}
                      disabled={slot.status === 'cancelled'}
                    >
                      Edit slot
                    </Button>
                  ) : null}
                  {slot.mode === 'online' && slot.locationOrLink ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={<Link2 size={14} aria-hidden="true" />}
                      onClick={() => {
                        void handleCopyInvite();
                      }}
                    >
                      Copy invite link
                    </Button>
                  ) : null}
                  {isSuperAdmin && slot.status !== 'cancelled' ? (
                    <Button
                      variant="danger"
                      size="sm"
                      icon={<Trash2 size={14} aria-hidden="true" />}
                      onClick={() => {
                        void handleCancelSlot();
                      }}
                      loading={cancelMutation.isPending}
                    >
                      Cancel slot
                    </Button>
                  ) : null}
                </>
              }
            />

            <KpiStrip items={kpis} />

            <div
              role="tablist"
              aria-label="Slot sections"
              className="flex flex-wrap gap-1 border-b border-[color:var(--color-border)]"
            >
              {TABS.map((opt) => {
                const active = tab === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setTab(opt.value)}
                    className={clsx(
                      'rounded-t-md px-4 py-2 text-sm font-medium transition-colors',
                      'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-500',
                      active
                        ? 'border-b-2 border-gold-500 text-[color:var(--color-text)]'
                        : 'text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)]',
                    )}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>

            {tab === 'overview' ? (
              <OverviewTab data={data} />
            ) : null}
            {tab === 'bookings' ? (
              <BookingsTab
                bookings={data.bookings}
                canAct={isHr}
                canBook={canBook}
                onBook={() => {
                  setPicked(null);
                  setBookOpen(true);
                }}
                onAttendance={(bookingId, status) =>
                  attendanceMutation.mutate({ bookingId, status })
                }
                attendanceTargetId={
                  attendanceMutation.isPending
                    ? attendanceMutation.variables?.bookingId ?? null
                    : null
                }
                onRelease={(bookingId) => {
                  void handleRelease(bookingId);
                }}
                releasingId={
                  releaseMutation.isPending
                    ? releaseMutation.variables?.bookingId ?? null
                    : null
                }
              />
            ) : null}
            {tab === 'evaluations' ? (
              <EvaluationsTab
                evaluations={data.evaluations}
                bookings={data.bookings}
              />
            ) : null}
            {tab === 'activity' ? (
              <ActivityPanel
                targetType="interview_slot"
                targetId={slot.id}
                title="Activity"
                emptyCopy="No slot activity recorded yet."
              />
            ) : null}
          </div>
        )}
      </GatedSection>

      <EditInterviewSlotDrawer
        open={editOpen}
        slot={slot ?? null}
        onClose={() => setEditOpen(false)}
        onSaved={() => {
          setEditOpen(false);
          invalidate();
        }}
      />

      <AddRelationModal<{ id: string }>
        open={bookOpen}
        onClose={() => setBookOpen(false)}
        title="Book a candidate"
        description="Pick a candidate at aptitude_passed and book them into this slot."
        submitLabel="Book candidate"
        canSubmit={picked !== null && picked.id !== ''}
        picker={
          <EntityPicker
            entity="application"
            {...(picked ? { value: picked } : {})}
            onChange={(next) => setPicked(Array.isArray(next) ? next[0] ?? null : next)}
            filter={{ pipelineStage: 'aptitude_passed' }}
            placeholder="Search candidate by name or email"
          />
        }
        onSubmit={handleBookSubmit}
        onSuccess={() => {
          setBookOpen(false);
          setPicked(null);
          invalidate();
          toast('Candidate booked.', { variant: 'success' });
          // Optimistically jump to the bookings tab so HR sees the new row.
          setTab('bookings');
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Overview tab
// ---------------------------------------------------------------------------

function OverviewTab({ data }: { data: InterviewSlotDetailResponse }) {
  const { slot, interviewer, createdBy } = data;
  return (
    <section className="grid gap-4 lg:grid-cols-2">
      <Card title="Interviewer">
        {interviewer ? (
          <div className="space-y-2">
            <p className="text-sm font-medium text-[color:var(--color-text)]">
              {interviewer.displayName}
            </p>
            {interviewer.email ? (
              <p className="text-xs text-[color:var(--color-text-muted)]">
                {interviewer.email}
              </p>
            ) : null}
            <p className="pt-1 text-[11px] text-[color:var(--color-text-subtle)] font-mono">
              {interviewer.id}
            </p>
          </div>
        ) : (
          <p className="text-sm text-[color:var(--color-text-muted)]">
            Interviewer user{' '}
            <code className="font-mono">{slot.interviewerId.slice(0, 8)}</code>{' '}
            (display name unavailable).
          </p>
        )}
      </Card>

      <Card title="Created by">
        {createdBy ? (
          <div className="space-y-2">
            <p className="text-sm font-medium text-[color:var(--color-text)]">
              {createdBy.displayName}
            </p>
            {createdBy.email ? (
              <p className="text-xs text-[color:var(--color-text-muted)]">
                {createdBy.email}
              </p>
            ) : null}
          </div>
        ) : (
          <p className="text-sm text-[color:var(--color-text-muted)]">
            Creator unavailable.
          </p>
        )}
      </Card>

      <Card title="Schedule" className="lg:col-span-2">
        <dl className="grid gap-3 sm:grid-cols-2">
          <Row label="Starts at">
            {formatDate(slot.startsAt, { withTime: true })}
          </Row>
          <Row label="Ends at">{formatDate(slot.endsAt, { withTime: true })}</Row>
          <Row label="Mode">{SLOT_MODE_LABEL[slot.mode]}</Row>
          <Row label="Capacity">{slot.capacity}</Row>
          <Row label="Location / link">{slot.locationOrLink ?? '—'}</Row>
          <Row label="Status">{SLOT_STATUS_LABEL[slot.status]}</Row>
        </dl>
        {slot.notes ? (
          <div className="mt-4 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-3 py-2 text-sm">
            <p className="text-[10px] uppercase tracking-wide text-[color:var(--color-text-muted)]">
              Notes for the candidate
            </p>
            <p className="mt-1 whitespace-pre-wrap text-[color:var(--color-text)]">
              {slot.notes}
            </p>
          </div>
        ) : null}
      </Card>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Bookings tab
// ---------------------------------------------------------------------------

interface BookingsTabProps {
  bookings: ReadonlyArray<InterviewBookingWithCandidate>;
  canAct: boolean;
  canBook: boolean;
  onBook: () => void;
  onAttendance: (bookingId: string, status: 'attended' | 'no_show') => void;
  attendanceTargetId: string | null;
  onRelease: (bookingId: string) => void;
  releasingId: string | null;
}

function BookingsTab({
  bookings,
  canAct,
  canBook,
  onBook,
  onAttendance,
  attendanceTargetId,
  onRelease,
  releasingId,
}: BookingsTabProps) {
  return (
    <RelationPanel
      title="Bookings"
      count={bookings.length}
      isEmpty={bookings.length === 0}
      empty={{
        copy: 'No candidates have booked this slot yet.',
        cta: canBook ? (
          <Button
            size="sm"
            icon={<UserPlus size={14} aria-hidden="true" />}
            onClick={onBook}
          >
            Book candidate
          </Button>
        ) : undefined,
      }}
      action={
        canBook ? (
          <Button
            size="sm"
            icon={<UserPlus size={14} aria-hidden="true" />}
            onClick={onBook}
          >
            Book candidate
          </Button>
        ) : null
      }
    >
      <div className="overflow-hidden rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg)]">
        <table className="w-full text-sm">
          <thead className="bg-[color:var(--color-surface)]">
            <tr>
              <Th>Candidate</Th>
              <Th>Status</Th>
              <Th>Booked</Th>
              <Th>
                <span className="sr-only">Actions</span>
              </Th>
            </tr>
          </thead>
          <tbody>
            {bookings.map((b) => {
              const attendancePending = attendanceTargetId === b.id;
              const releasing = releasingId === b.id;
              const canMutate =
                canAct && b.status !== 'cancelled' && b.status !== 'no_show';
              return (
                <tr
                  key={b.id}
                  className="border-t border-[color:var(--color-border)]"
                >
                  <td className="px-4 py-2">
                    <Link
                      to="/hr/applications/$applicationId"
                      params={{ applicationId: b.applicationId }}
                      className="block min-w-0"
                    >
                      {b.application ? (
                        <>
                          <p className="truncate font-medium text-[color:var(--color-text)] hover:underline">
                            {b.application.fullName}
                          </p>
                          <p className="truncate text-xs text-[color:var(--color-text-muted)]">
                            {b.application.email}
                          </p>
                        </>
                      ) : (
                        <span className="font-mono text-[11px] text-gold-700 hover:underline">
                          {b.applicationId.slice(0, 8)}…
                        </span>
                      )}
                    </Link>
                  </td>
                  <td className="px-4 py-2 text-xs">
                    <span className="inline-flex items-center rounded-full border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-2 py-0.5 uppercase tracking-wide text-[color:var(--color-text-muted)]">
                      {b.status}
                    </span>
                    {b.cancelledReason ? (
                      <span className="ml-2 text-[10px] text-[color:var(--color-text-subtle)]">
                        {b.cancelledReason}
                      </span>
                    ) : null}
                  </td>
                  <td className="px-4 py-2 text-xs text-[color:var(--color-text-muted)] tabular-nums">
                    {timeAgo(b.createdAt)}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {canMutate ? (
                      <div className="inline-flex flex-wrap items-center justify-end gap-1.5">
                        {b.status === 'booked' ? (
                          <>
                            <Button
                              size="sm"
                              variant="secondary"
                              icon={<CheckCircle2 size={12} aria-hidden="true" />}
                              loading={
                                attendancePending && attendanceTargetId === b.id
                              }
                              disabled={attendancePending}
                              onClick={() => onAttendance(b.id, 'attended')}
                            >
                              Attended
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              icon={<XCircle size={12} aria-hidden="true" />}
                              disabled={attendancePending}
                              onClick={() => onAttendance(b.id, 'no_show')}
                            >
                              No-show
                            </Button>
                          </>
                        ) : null}
                        <Button
                          size="sm"
                          variant="ghost"
                          icon={<Trash2 size={12} aria-hidden="true" />}
                          loading={releasing}
                          onClick={() => onRelease(b.id)}
                          className="text-[color:var(--color-text-muted)]"
                        >
                          Release
                        </Button>
                      </div>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </RelationPanel>
  );
}

// ---------------------------------------------------------------------------
// Evaluations tab
// ---------------------------------------------------------------------------

interface EvaluationsTabProps {
  evaluations: ReadonlyArray<InterviewEvaluation>;
  bookings: ReadonlyArray<InterviewBookingWithCandidate>;
}

function EvaluationsTab({ evaluations, bookings }: EvaluationsTabProps) {
  const bookingMap = useMemo(() => {
    const map = new Map<string, InterviewBookingWithCandidate>();
    for (const b of bookings) map.set(b.id, b);
    return map;
  }, [bookings]);

  if (evaluations.length === 0) {
    return (
      <EmptyState
        icon={<Calendar size={28} aria-hidden="true" />}
        title="No evaluations yet"
        description="Evaluations appear once an interviewer scores a booking."
      />
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)]">
      <table className="w-full text-sm">
        <thead className="bg-[color:var(--color-surface)]">
          <tr>
            <Th>Candidate</Th>
            <Th>Decision</Th>
            <Th>Overall</Th>
            <Th>Decided</Th>
            <Th>
              <span className="sr-only">Booking</span>
            </Th>
          </tr>
        </thead>
        <tbody>
          {evaluations.map((e) => {
            const b = bookingMap.get(e.bookingId);
            return (
              <tr
                key={e.id}
                className="border-t border-[color:var(--color-border)]"
              >
                <td className="px-4 py-2">
                  {b ? (
                    <Link
                      to="/hr/applications/$applicationId"
                      params={{ applicationId: b.applicationId }}
                      className="font-medium text-[color:var(--color-text)] hover:underline"
                    >
                      {b.application ? b.application.fullName : `application ${b.applicationId.slice(0, 8)}…`}
                    </Link>
                  ) : (
                    <span className="font-mono text-[11px] text-[color:var(--color-text-muted)]">
                      booking {e.bookingId.slice(0, 8)}…
                    </span>
                  )}
                </td>
                <td className="px-4 py-2">
                  <span className="inline-flex items-center rounded-full border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-2 py-0.5 text-[11px] uppercase tracking-wide">
                    {INTERVIEW_DECISION_LABEL[e.decision]}
                  </span>
                </td>
                <td className="px-4 py-2 tabular-nums">
                  {e.overallScore !== null ? e.overallScore : '—'}
                </td>
                <td className="px-4 py-2 text-xs text-[color:var(--color-text-muted)]">
                  {e.decidedAt ? formatDate(e.decidedAt, { withTime: true }) : '—'}
                </td>
                <td className="px-4 py-2 text-right text-xs">
                  {b ? (
                    <Link
                      to="/hr/applications/$applicationId"
                      params={{ applicationId: b.applicationId }}
                      className="text-gold-700 hover:underline"
                    >
                      Open booking
                    </Link>
                  ) : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

function Card({
  title,
  children,
  className,
}: {
  title: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={clsx(
        'rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-5',
        className,
      )}
    >
      <h3 className="text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
        {title}
      </h3>
      <div className="mt-3">{children}</div>
    </section>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-[color:var(--color-text-muted)]">
        {label}
      </dt>
      <dd className="mt-1 text-sm text-[color:var(--color-text)]">{children}</dd>
    </div>
  );
}

function Th({ children }: { children: ReactNode }) {
  return (
    <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
      {children}
    </th>
  );
}
